import { NgTemplateOutlet } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxColor, OnyxOptional, OnyxTab } from '@onyx/angular';
import {
  ActionHelper,
  OnyxButtonComponent,
  OnyxDataWidgetComponent,
  OnyxDatePipe,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxInformationHeadingComponent,
  OnyxMessageComponent,
  OnyxNamePipe,
  OnyxProgressComponent,
  OnyxTabsComponent,
  OnyxTimePipe,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { chain } from 'lodash';
import { DateTime } from 'luxon';
import type { Subject } from 'rxjs';
import { AuthStore } from '../../../auth/common/stores/auth.store';
import { DriverHelper } from '../../../dashboard/drivers/common/helpers/driver.helper';
import type {
  Driver,
  SimplifiedDriver,
} from '../../../dashboard/drivers/common/interfaces/driver';
import type { Fleet } from '../../../dashboard/fleet/common/interfaces/fleet';
import { IntegrationsRoute } from '../../../dashboard/management-panel/integrations/common/enums/integrations-route';
import { TelematicsHelper } from '../../../dashboard/map/common/helpers/telematics.helper';
import { TELEMATICS_OFFLINE_THRESHOLD } from '../../constants/telematics/telematics-offline-threshold';
import { TELEMATICS_OUTDATED_THRESHOLD } from '../../constants/telematics/telematics-outdated-threshold';
import { DictionaryCode } from '../../enums/dictionary-code';
import { TachographWorkingMode } from '../../enums/tachograph-working-mode';
import { CommonHelper } from '../../helpers/common.helper';
import type { DurationPipeOptions } from '../../interfaces/common/duration-options';
import type { TachographTimeCounter } from '../../interfaces/common/tachograph';
import type { TelematicsDriver } from '../../interfaces/telematics/telematics-driver';
import { DurationPipe } from '../../pipes/duration.pipe';
import { UnavailabilityIconComponent } from '../unavailabilities/unavailability-icon/unavailability-icon.component';
import { ModuleLinkComponent } from '../module-link/module-link.component';

interface TachographItemData extends TachographTimeCounter {
  isLimitExceeded: boolean;
  isOutdated: boolean;
  supheading: string;
  supheadingDetails: string | null;
  tooltip: string | null;
  progressBarColor: OnyxColor;
}

export type TachographDriver = Driver | SimplifiedDriver | TelematicsDriver;

@Component({
  selector: 'app-drivers-tachograph',
  imports: [
    ModuleLinkComponent,
    OnyxTabsComponent,
    OnyxIconComponent,
    OnyxDataWidgetComponent,
    TranslatePipe,
    OnyxIconButtonComponent,
    OnyxTooltipDirective,
    OnyxMessageComponent,
    OnyxNamePipe,
    DurationPipe,
    OnyxProgressComponent,
    RouterLink,
    OnyxButtonComponent,
    UnavailabilityIconComponent,
    NgTemplateOutlet,
    OnyxInformationHeadingComponent,
    DurationPipe,
  ],
  templateUrl: './drivers-tachograph.component.html',
  styleUrl: './drivers-tachograph.component.scss',
})
export class DriversTachographComponent<
  T extends TachographDriver,
> implements OnInit {
  protected readonly authStore = inject(AuthStore);
  private readonly driverHelper = inject(DriverHelper);
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);
  private readonly durationPipe = inject(DurationPipe);
  private readonly timePipe = inject(OnyxTimePipe);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly commonHelper = inject(CommonHelper);

  public readonly primaryDriver = input.required<OnyxOptional<T>>();
  public readonly secondaryDriver = input.required<OnyxOptional<T>>();
  public readonly vehicle = input<OnyxOptional<Fleet>>(null);
  public readonly type = input.required<'vehicle' | 'order'>();
  public readonly orientation = input<'horizontal' | 'vertical'>('horizontal');
  public readonly compact = input(false);
  public readonly showHeading = input(true);
  public readonly fixedHeadingHeight = input(false);
  public readonly serviceTime = input<OnyxOptional<number>>(null);
  public readonly close$ = input<Subject<void>>();

  public readonly driverChange = output<T>();

  protected readonly I18N = 'components.driversTachograph';
  protected readonly DURATION_OPTIONS: DurationPipeOptions = {
    units: ['h', 'm'],
  };

  protected readonly ActionHelper = ActionHelper;
  protected readonly TachographWorkingMode = TachographWorkingMode;
  protected readonly CommonHelper = CommonHelper;
  protected readonly IntegrationsRoute = IntegrationsRoute;

  protected drivers = computed(() =>
    chain([this.primaryDriver(), this.secondaryDriver()]).compact().value(),
  );
  protected tabs = computed<OnyxTab<T>[]>(() =>
    this.drivers().map((driver) => ({
      value: driver,
      icon: { name: TelematicsHelper.getDriverIcon(driver), size: 20 },
      tooltip: DriverHelper.getSelectedDriverTooltip(driver, null),
    })),
  );

  protected selectedDriver = linkedSignal<T[], T>({
    source: () => this.drivers(),
    computation: (drivers, previous) =>
      drivers.find(({ uuid }) => uuid === previous?.value?.uuid) ?? drivers[0],
  });
  protected selectedDriverIcon = computed(() => ({
    name: TelematicsHelper.getDriverIcon(this.selectedDriver()),
    tooltip: DriverHelper.getSelectedDriverTooltip(this.selectedDriver(), null),
  }));

  protected tachograph = computed(() => this.selectedDriver()?.tachograph);

  protected timeSinceLastSignal = computed(() => {
    const updatedAt = this.tachograph()?.updatedAt;
    if (!updatedAt) return 0;

    return DateTime.now().diff(DateTime.fromISO(updatedAt)).as('seconds');
  });

  protected outdatedData = computed(() => {
    const updatedAt = this.tachograph()?.updatedAt;
    if (!updatedAt) return null;

    const diff = this.commonHelper
      .nowMinute()
      .diff(DateTime.fromISO(updatedAt));
    if (diff < TELEMATICS_OFFLINE_THRESHOLD) return null;

    return {
      duration: diff,
      showIcon: diff >= TELEMATICS_OUTDATED_THRESHOLD,
    };
  });

  protected hasTelematics = computed(() => {
    const selectedDriver = this.selectedDriver();
    return 'telematics' in selectedDriver ? !!selectedDriver.telematics : false;
  });

  protected workingModeData = computed<TachographItemData | null>(() => {
    const tachograph = this.tachograph();
    if (!tachograph) return null;

    const { workingMode } = tachograph;
    let time: TachographTimeCounter;
    let estimatedEnd: DateTime | null;

    if (workingMode === TachographWorkingMode.RIDE) {
      time = tachograph.continuousDriving;
      estimatedEnd = DateTime.fromISO(tachograph.updatedAt).plus({
        seconds: time.effectiveRemaining,
      });
    } else {
      const start = DateTime.fromISO(tachograph.workingModeStartedAt);
      const referenceTime = this.outdatedData()
        ? DateTime.fromISO(tachograph.updatedAt)
        : DateTime.now();

      const duration = {
        [TachographWorkingMode.RIDE]: null,
        [TachographWorkingMode.OTHER_WORK]: this.serviceTime(),
        [TachographWorkingMode.BREAK]: tachograph.estimatedBreakEndAt
          ? DateTime.fromISO(tachograph.estimatedBreakEndAt)
              .diff(start)
              .as('seconds')
          : null,
        [TachographWorkingMode.AVAILABILITY]: null,
        [TachographWorkingMode.OFFLINE]: null,
      }[workingMode];

      estimatedEnd = duration ? start.plus({ seconds: duration }) : null;

      const elapsed = start.diff(referenceTime).negate().as('seconds');
      const remaining = estimatedEnd
        ? estimatedEnd.diff(referenceTime).as('seconds')
        : null;

      time = {
        elapsed,
        remaining,
        effectiveElapsed: elapsed,
        effectiveRemaining: remaining ?? Number.MAX_SAFE_INTEGER,
        limit: duration ?? Number.MAX_SAFE_INTEGER,
      };
    }

    const supheading = {
      [TachographWorkingMode.RIDE]: 'labels.continuousDriving',
      [TachographWorkingMode.OTHER_WORK]: `${DictionaryCode.TACHOGRAPH_WORKING_MODE}.${TachographWorkingMode.OTHER_WORK}`,
      [TachographWorkingMode.AVAILABILITY]: `${DictionaryCode.TACHOGRAPH_WORKING_MODE}.${TachographWorkingMode.AVAILABILITY}`,
      [TachographWorkingMode.BREAK]: `${DictionaryCode.TACHOGRAPH_WORKING_MODE}.${TachographWorkingMode.BREAK}`,
      [TachographWorkingMode.OFFLINE]: 'labels.noData',
    }[workingMode];

    const isLimitExceeded = time.effectiveElapsed > time.limit;
    const isOutdated = this.timeSinceLastSignal() > time.effectiveRemaining;

    let tooltip: string | null;
    if (workingMode === TachographWorkingMode.OFFLINE) {
      tooltip = null;
    } else if (workingMode === TachographWorkingMode.AVAILABILITY) {
      tooltip = this.translateService.instant(
        `${this.I18N}.tooltips.availabilityElapsed`,
        {
          elapsed: this.durationPipe.transform(
            time.effectiveElapsed,
            this.DURATION_OPTIONS,
          ),
        },
      );
    } else if (time.limit === Number.MAX_SAFE_INTEGER) {
      tooltip = this.translateService.instant(
        `${this.I18N}.tooltips.${workingMode}.elapsedLimitUnknown`,
        {
          elapsed: this.durationPipe.transform(
            time.effectiveElapsed,
            this.DURATION_OPTIONS,
          ),
        },
      );
    } else {
      tooltip = this.getTimeTooltip(
        workingMode,
        time,
        isLimitExceeded,
        estimatedEnd,
      );
    }

    const progressBarColor = (
      {
        [TachographWorkingMode.RIDE]: 'blue',
        [TachographWorkingMode.OTHER_WORK]: 'violet',
        [TachographWorkingMode.BREAK]: 'orange',
        [TachographWorkingMode.AVAILABILITY]: 'blue',
        [TachographWorkingMode.OFFLINE]: 'blue',
      } as const
    )[workingMode];

    return {
      ...time,
      isLimitExceeded,
      isOutdated,
      supheading,
      supheadingDetails: null,
      tooltip,
      progressBarColor,
    };
  });

  protected dailyDrivingData = computed<TachographItemData | null>(() => {
    const tachograph = this.tachograph();
    if (!tachograph) return null;

    const time = tachograph.dailyDriving;
    const isLimitExceeded = time.effectiveElapsed > time.limit;
    const isOutdated = this.timeSinceLastSignal() > time.effectiveRemaining;

    const workdayStartTime = tachograph.dailyAmplitude.elapsed
      ? DateTime.fromISO(tachograph.updatedAt).minus({
          seconds: tachograph.dailyAmplitude.elapsed,
        })
      : null;
    const remainingDailyDrivingTimeExtensions =
      tachograph.dailyDrivingTimeExtensions.remaining;
    const remainingReducedDailyRestExtensions =
      tachograph.reducedDailyRestExtensions.remaining;

    const drivingTooltip = this.getTimeTooltip(
      'driving',
      time,
      isLimitExceeded,
    );
    const tooltip = [
      drivingTooltip,
      this.translateService.instant(
        `${this.I18N}.tooltips.dailyDrivingTimeExtensions.${
          remainingDailyDrivingTimeExtensions != null ? 'value' : 'unknown'
        }`,
        { value: remainingDailyDrivingTimeExtensions },
      ),
      this.translateService.instant(
        `${this.I18N}.tooltips.reducedDailyRestExtensions.${
          remainingReducedDailyRestExtensions != null ? 'value' : 'unknown'
        }`,
        { value: remainingReducedDailyRestExtensions },
      ),
      this.translateService.instant(
        `${this.I18N}.tooltips.workdayStartTime.${
          workdayStartTime ? 'value' : 'unknown'
        }`,
        { value: this.timePipe.transform(workdayStartTime) },
      ),
    ].join('</br>');

    return {
      ...time,
      isLimitExceeded,
      isOutdated,
      supheading: 'labels.dailyDriving',
      supheadingDetails:
        tachograph.dailyDrivingTimeExtensions.remaining != null
          ? `+${tachograph.dailyDrivingTimeExtensions.remaining}`
          : '-',
      tooltip,
      progressBarColor: 'blue',
    };
  });

  protected weeklyDrivingData = computed<TachographItemData | null>(() => {
    const tachograph = this.tachograph();
    if (!tachograph) return null;

    const time = tachograph.weeklyDriving;
    const isLimitExceeded = time.effectiveElapsed > time.limit;
    const isOutdated = this.timeSinceLastSignal() > time.effectiveRemaining;

    const workWeekStartDate = tachograph.workWeek.elapsed
      ? DateTime.fromISO(tachograph.updatedAt).minus({
          seconds: tachograph.workWeek.elapsed,
        })
      : null;

    const drivingTooltip = this.getTimeTooltip(
      'driving',
      time,
      isLimitExceeded,
    );
    const tooltip = [
      drivingTooltip,
      this.translateService.instant(
        `${this.I18N}.tooltips.workWeekStartDate.${workWeekStartDate != null ? 'value' : 'unknown'}`,
        { value: this.datePipe.transform(workWeekStartDate, 'day-date') },
      ),
    ].join('</br>');

    return {
      ...time,
      isLimitExceeded,
      isOutdated,
      supheading: 'labels.thisWeek',
      supheadingDetails: null,
      tooltip,
      progressBarColor: 'blue',
    };
  });

  protected fortnightDrivingData = computed<TachographItemData | null>(() => {
    const tachograph = this.tachograph();
    if (!tachograph) return null;

    const time = tachograph.fortnightDriving;
    const isLimitExceeded = time.effectiveElapsed > time.limit;
    const isOutdated = this.timeSinceLastSignal() > time.effectiveRemaining;

    return {
      ...time,
      isLimitExceeded,
      isOutdated,
      supheading: 'labels.twoWeeks',
      supheadingDetails: null,
      tooltip: this.getTimeTooltip('driving', time, isLimitExceeded),
      progressBarColor: 'blue',
    };
  });

  public ngOnInit(): void {
    effect(() => this.driverChange.emit(this.selectedDriver()), {
      injector: this.injector,
    });
  }

  protected openDriverModal(driver: T): void {
    this.driverHelper.openModal(driver.uuid);
  }

  private getTimeTooltip(
    mode: string,
    time: TachographTimeCounter,
    isLimitExceeded: boolean,
    estimatedEnd: DateTime | null = null,
  ): string {
    const i18n = `${this.I18N}.tooltips.${mode}`;

    return [
      this.translateService.instant(`${i18n}.elapsed`, {
        elapsed: this.durationPipe.transform(
          time.effectiveElapsed,
          this.DURATION_OPTIONS,
        ),
        limit: this.durationPipe.transform(time.limit, this.DURATION_OPTIONS),
      }),
      !isLimitExceeded
        ? this.translateService.instant(`${i18n}.remaining`, {
            remaining: this.durationPipe.transform(
              time.effectiveRemaining,
              this.DURATION_OPTIONS,
            ),
            ...(estimatedEnd && {
              estimatedEnd: CommonHelper.isSameDay(estimatedEnd)
                ? this.timePipe.transform(estimatedEnd)
                : this.datePipe.transform(estimatedEnd, 'time-date-dot'),
            }),
          })
        : this.translateService.instant(`${i18n}.limitExceeded`),
    ].join('</br>');
  }
}
